package com.htmlagent.shared.auth

import com.htmlagent.shared.UiLocale
import kotlinx.serialization.Serializable

/** Asks for an access link. [locale] picks the email's language. */
@Serializable
data class LoginLinkRequest(val email: String, val locale: UiLocale = UiLocale.SPANISH)

/**
 * The link is on its way. [match] is the number this browser shows while it waits: if the link is
 * opened in another browser, picking it there lets this one in.
 */
@Serializable
data class LinkSentResponse(val match: Int)

/**
 * The token from the emailed link, sent by the page and not by a `GET` on the link itself: mail
 * scanners follow links, and a single-use link opened by one of them would be spent.
 */
@Serializable
data class VerifyLinkRequest(val token: String)

/**
 * What opening an access link came to: signed in as [me] in the browser that asked for it, or, in
 * any other, [approve]: the numbers to pick from to let the waiting browser in.
 */
@Serializable
data class VerifyLinkResponse(val me: MeResponse? = null, val approve: ApprovalPrompt? = null)

/** Whose account the link is for, and the numbers offered; one is what the waiting browser shows. */
@Serializable
data class ApprovalPrompt(val email: String, val choices: List<Int>)

/** Approves the waiting browser with the number picked. A wrong one spends the link. */
@Serializable
data class ApproveLinkRequest(val token: String, val match: Int)

/**
 * Where the browser waiting for its link stands: signed in as [me], or still [waiting]. The server
 * keeps what the waiting screen shows, so a window closed while waiting shows it again on reopening.
 */
@Serializable
data class WaitResponse(val me: MeResponse? = null, val waiting: WaitingLink? = null)

/** The link this browser is waiting for: where it went, and the number to pick if it opens elsewhere. */
@Serializable
data class WaitingLink(val email: String, val match: Int)

/** [recoveryKey] resumes an existing guest; without it a new one is created. */
@Serializable
data class GuestRequest(val recoveryKey: String? = null)

/**
 * Who is signed in. Exactly one of the two is set: [email] for an account, [guestKey] for a
 * guest. The key is shown only to its owner, so they can save it and come back on another browser.
 */
@Serializable
data class MeResponse(val email: String? = null, val guestKey: String? = null)
